import { useEffect, useState, useCallback } from "react";

// Theme lives on <html data-theme="light|dark">.
// Default: follow the OS/browser setting (prefers-color-scheme), live.
// Visitors pick Light / Dark / Auto from the theme menu; Light or Dark is
// saved in localStorage and wins, Auto clears it to follow the system again.
const STORAGE_KEY = "loadout-theme-pref";
// Legacy key auto-saved "light" for every visitor; only "dark" was a real choice.
const LEGACY_KEY = "loadout-theme";
const MQ = "(prefers-color-scheme: dark)";
const SYNC_EVENT = "loadout-theme-change";

function readStored() {
  try {
    const legacy = window.localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) {
      window.localStorage.removeItem(LEGACY_KEY);
      if (legacy === "dark" && !window.localStorage.getItem(STORAGE_KEY)) {
        window.localStorage.setItem(STORAGE_KEY, "dark");
      }
    }
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null;
  }
}

function systemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia(MQ).matches ? "dark" : "light";
}

export function readTheme() {
  if (typeof window === "undefined") return "light";
  return readStored() || systemTheme();
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function useTheme() {
  const [pref, setPref] = useState(readStored);
  const [sys, setSys] = useState(systemTheme);
  const theme = pref || sys;

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia ? window.matchMedia(MQ) : null;
    const onSys = () => setSys(systemTheme());
    const onPref = () => setPref(readStored());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) onPref(); };
    mq?.addEventListener?.("change", onSys);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SYNC_EVENT, onPref);
    return () => {
      mq?.removeEventListener?.("change", onSys);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SYNC_EVENT, onPref);
    };
  }, []);

  // next: "light" | "dark" | "system". A pick equal to the current system
  // theme is stored as an override anyway when chosen explicitly from the
  // menu; "system" clears it.
  const setTheme = useCallback((next) => {
    try {
      if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch { /* ignore */ }
    setPref(readStored());
    window.dispatchEvent(new Event(SYNC_EVENT));
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, mode: pref || "system", isDark: theme === "dark", isSystem: !pref, toggle, setTheme };
}
